import { useCallback, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useLocalParticipant } from "@livekit/components-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCallPictureInPicture, type CallPip } from "../hooks/useCallPictureInPicture";
import { useDeviceToggles, type DeviceToggles } from "../hooks/useDeviceToggles";
import { useRoom } from "../hooks/useVideo";
import { useWaitingGuests } from "../hooks/useWaitingGuests";
import { CallPipContext } from "../lib/callPipContext";
import { playCallEnd } from "../lib/sound";
import { useCallSessionStore } from "../store/callSessionStore";
import { CallPipWindow } from "./CallPipWindow";

interface CallPipHostProps {
  slug: string;
  roomId: string;
  liveKitToken: string;
  children: ReactNode;
}

/**
 * Owns the call's picture-in-picture window for the whole call. Mounted by
 * CallRouteAwareRenderer at a fixed position around the InCallView ↔
 * FloatingCallWidget swap, so changing suite modules only changes this
 * component's children — the controller hook (and the open window) survive.
 *
 * Provides the controller to its children (the bottom-bar "Pop out" button)
 * and, in the document tier, portals CallPipWindow into the PiP document.
 */
export function CallPipHost({ slug, roomId, liveKitToken, children }: CallPipHostProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clear = useCallSessionStore((s) => s.clear);
  const markLeaveIntent = useCallSessionStore((s) => s.markLeaveIntent);
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();

  // Called unconditionally, not gated on the window being open: the waiting
  // label feeds the PiP hook itself, so gating on its state would be circular.
  const { data: room } = useRoom(roomId, { pollIntervalMs: false });
  const mayAdmit = Boolean(room?.may_admit_guests);
  const { waitingCount } = useWaitingGuests(roomId, liveKitToken, mayAdmit);
  const waitingLabel = waitingCount > 0 ? t("call.pip.waiting", { count: waitingCount }) : null;

  // The floating window has no room for the in-call device affordances, so a
  // failed enable surfaces as a toast instead (disabling never fails visibly).
  const rawToggles = useDeviceToggles(localParticipant);
  const { toggleMic: rawToggleMic, toggleCamera: rawToggleCamera } = rawToggles;
  const toggleMic = useCallback(
    async (next: boolean) => {
      const ok = await rawToggleMic(next);
      if (!ok && next) toast.error(t("call.deviceAffordance.microphone.unknown"));
      return ok;
    },
    [rawToggleMic, t],
  );
  const toggleCamera = useCallback(
    async (next: boolean) => {
      const ok = await rawToggleCamera(next);
      if (!ok && next) toast.error(t("call.deviceAffordance.camera.unknown"));
      return ok;
    },
    [rawToggleCamera, t],
  );
  const toggles: DeviceToggles = { ...rawToggles, toggleMic, toggleCamera };

  // Deliberate leave: flag intent BEFORE clearing so the disconnect is not
  // auto-rejoined (#1178). Clearing unmounts this host, which closes the window.
  const onEnd = useCallback(() => {
    markLeaveIntent();
    playCallEnd();
    clear();
  }, [markLeaveIntent, clear]);

  const pip = useCallPictureInPicture({
    waitingLabel,
    onToggleMic: () => void toggleMic(!isMicrophoneEnabled),
    onToggleCamera: () => void toggleCamera(!isCameraEnabled),
    onHangUp: onEnd,
    // Phones: back from the floating video means "show me the call".
    onReturnToCall: () => navigate(`/call/${slug}`),
  });
  const { tier, isOpen, pipWindow, open, close } = pip;
  const value = useMemo<CallPip>(
    () => ({ tier, isOpen, pipWindow, open, close }),
    [tier, isOpen, pipWindow, open, close],
  );

  const onReturn = useCallback(() => {
    window.focus();
    navigate(`/call/${slug}`);
    close();
  }, [navigate, slug, close]);

  return (
    <CallPipContext.Provider value={value}>
      {children}
      {isOpen && pipWindow
        ? createPortal(
            <CallPipWindow
              waitingCount={waitingCount}
              onReturn={onReturn}
              onEnd={onEnd}
              toggles={toggles}
            />,
            pipWindow.document.body,
          )
        : null}
    </CallPipContext.Provider>
  );
}
